import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Pencil, Loader2, Archive, RotateCcw, Heading2, Minus, FileText as StaticIcon, CircleDot, ListChecks, Paperclip, PenLine, Eye, EyeOff } from 'lucide-react';
import { OPERATOR_LABELS, VISIBILITY_OP_BY_PARENT_TYPE, VISIBILITY_ELIGIBLE_PARENT_TYPES } from '../lib/formVisibility';

// Phase 1a (Advanced Inspection Forms): expanded type catalog grouped by purpose.
// Phase 1b: file uploads + multiple signature fields.
const FIELD_TYPES = [
  // Inputs
  { value: 'text', label: 'Text (single line)', group: 'Input' },
  { value: 'textarea', label: 'Text (multi-line)', group: 'Input' },
  { value: 'number', label: 'Number', group: 'Input' },
  { value: 'date', label: 'Date', group: 'Input' },
  { value: 'file', label: 'File / Photo Upload', group: 'Input' },
  // Choice
  { value: 'checkbox', label: 'Checkbox (yes/no)', group: 'Choice' },
  { value: 'select', label: 'Dropdown Select', group: 'Choice' },
  { value: 'radio', label: 'Radio Group', group: 'Choice' },
  { value: 'checkbox_group', label: 'Multi-select Checkboxes', group: 'Choice' },
  // Signature (in-form, distinct from the customer-signature-link workflow)
  { value: 'signature', label: 'Signature Field', group: 'Signature' },
  // Layout (no value collected — visual only)
  { value: 'section_header', label: 'Section Header', group: 'Layout' },
  { value: 'divider', label: 'Divider Line', group: 'Layout' },
  { value: 'static', label: 'Static Text / Instructions', group: 'Layout' },
];

const LAYOUT_TYPES = new Set(['section_header', 'divider', 'static']);
const OPTION_TYPES = new Set(['select', 'radio', 'checkbox_group']);

// Radio first-class presets for inspection workflows.
const RADIO_PRESETS = [
  { key: 'pass_advisory_fail', label: 'Pass / Advisory / Fail', options: ['Pass', 'Advisory', 'Fail'] },
  { key: 'pass_fail_na', label: 'Pass / Fail / N/A', options: ['Pass', 'Fail', 'N/A'] },
  { key: 'yes_no_na', label: 'Yes / No / N/A', options: ['Yes', 'No', 'N/A'] },
  { key: 'yes_no', label: 'Yes / No', options: ['Yes', 'No'] },
  { key: 'custom', label: 'Custom (enter your own)', options: [] },
];

function FieldTypeIcon({ type }) {
  if (type === 'section_header') return <Heading2 className="w-3.5 h-3.5 text-slate-500" />;
  if (type === 'divider') return <Minus className="w-3.5 h-3.5 text-slate-500" />;
  if (type === 'static') return <StaticIcon className="w-3.5 h-3.5 text-slate-500" />;
  if (type === 'radio') return <CircleDot className="w-3.5 h-3.5 text-slate-500" />;
  if (type === 'checkbox_group') return <ListChecks className="w-3.5 h-3.5 text-slate-500" />;
  if (type === 'file') return <Paperclip className="w-3.5 h-3.5 text-slate-500" />;
  if (type === 'signature') return <PenLine className="w-3.5 h-3.5 text-slate-500" />;
  const labels = { text: 'Aa', textarea: 'Tx', number: '#', checkbox: 'Cb', select: 'Dd', date: 'Dt' };
  return <span className="text-[10px] font-mono bg-muted px-1.5 py-0.5 rounded">{labels[type] || '?'}</span>;
}

function detectPresetKey(options) {
  if (!Array.isArray(options) || options.length === 0) return 'custom';
  const joined = options.map(o => String(o).trim().toLowerCase()).join('|');
  for (const p of RADIO_PRESETS) {
    if (p.key === 'custom') continue;
    if (p.options.map(o => o.toLowerCase()).join('|') === joined) return p.key;
  }
  return 'custom';
}

const initialFieldForm = () => ({
  label: '',
  field_type: 'text',
  is_required: false,
  placeholder: '',
  options: '',
  preset_key: 'pass_advisory_fail',
  content_markdown: '',
  // Phase 2 — conditional visibility (single-clause)
  vis_enabled: false,
  vis_field_key: '',
  vis_operator: '',
  vis_value: '',
  // Phase 3 — repeatable sections (only meaningful for section_header type)
  repeatable: false,
  repeat_label: '',
  max_instances: 20,
});

export function FormTemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddField, setShowAddField] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [showEditMeta, setShowEditMeta] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [fieldForm, setFieldForm] = useState(initialFieldForm());

  const fetchTemplate = async () => {
    try {
      const res = await formsAPI.getTemplate(id);
      setTemplate(res.data);
    } catch { toast.error('Template not found'); navigate('/forms'); }
    setLoading(false);
  };

  useEffect(() => { fetchTemplate(); }, [id]);

  const resetFieldForm = () => setFieldForm(initialFieldForm());

  const buildFieldPayload = () => {
    const t = fieldForm.field_type;
    const data = {
      label: fieldForm.label.trim(),
      field_type: t,
      is_required: LAYOUT_TYPES.has(t) ? false : fieldForm.is_required,
      placeholder: (LAYOUT_TYPES.has(t) ? null : fieldForm.placeholder.trim()) || null,
      config: {},
    };
    if (OPTION_TYPES.has(t)) {
      let opts;
      if (t === 'radio' && fieldForm.preset_key && fieldForm.preset_key !== 'custom') {
        const preset = RADIO_PRESETS.find(p => p.key === fieldForm.preset_key);
        opts = preset ? preset.options.slice() : [];
      } else {
        opts = fieldForm.options.split('\n').map(o => o.trim()).filter(Boolean);
      }
      if (opts.length === 0) {
        toast.error(`${t === 'radio' ? 'Radio' : t === 'checkbox_group' ? 'Multi-select' : 'Select'} field needs at least one option`);
        return null;
      }
      data.options = opts;
      if (t === 'radio') {
        data.config.preset = fieldForm.preset_key || 'custom';
      }
    }
    if (t === 'static') {
      data.config.content_markdown = fieldForm.content_markdown || '';
    }
    // Phase 3 — repeatable section header
    if (t === 'section_header' && fieldForm.repeatable) {
      const lab = (fieldForm.repeat_label || '').trim();
      if (!lab) {
        toast.error('Enter an instance label (e.g. "Sliding Gate").');
        return null;
      }
      const max = Math.max(1, Math.min(50, Number(fieldForm.max_instances) || 20));
      data.config.repeatable = true;
      data.config.repeat_label = lab;
      data.config.max_instances = max;
    }
    // Phase 2 — visibility
    if (fieldForm.vis_enabled) {
      if (!fieldForm.vis_field_key || !fieldForm.vis_operator) {
        toast.error('Pick a controlling field and operator for the visibility rule');
        return null;
      }
      const op = fieldForm.vis_operator;
      const valueRequired = ['equals', 'not_equals', 'contains'].includes(op);
      if (valueRequired && (fieldForm.vis_value === '' || fieldForm.vis_value === null || fieldForm.vis_value === undefined)) {
        toast.error('Enter the value for the visibility rule');
        return null;
      }
      data.config.visible_if = {
        field_key: fieldForm.vis_field_key,
        operator: op,
        value: valueRequired ? fieldForm.vis_value : null,
      };
    }
    if (Object.keys(data.config).length === 0) data.config = null;
    return data;
  };

  const handleAddField = async (e) => {
    e.preventDefault();
    if (!fieldForm.label.trim()) return;
    const data = buildFieldPayload();
    if (!data) return;
    setSaving(true);
    try {
      await formsAPI.addField(id, data);
      toast.success('Field added');
      setShowAddField(false);
      resetFieldForm();
      fetchTemplate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add field');
    }
    setSaving(false);
  };

  const handleUpdateField = async (e) => {
    e.preventDefault();
    if (!editingField || !fieldForm.label.trim()) return;
    const data = buildFieldPayload();
    if (!data) return;
    setSaving(true);
    try {
      await formsAPI.updateField(id, editingField.id, data);
      toast.success('Field updated');
      setEditingField(null);
      resetFieldForm();
      fetchTemplate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
    setSaving(false);
  };

  const handleDeleteField = async (fieldId) => {
    try {
      await formsAPI.deleteField(id, fieldId);
      toast.success('Field removed');
      fetchTemplate();
    } catch { toast.error('Failed to remove'); }
  };

  const handleReorder = async (fieldIdx, direction) => {
    const fields = [...(template.fields || [])];
    const newIdx = fieldIdx + direction;
    if (newIdx < 0 || newIdx >= fields.length) return;
    [fields[fieldIdx], fields[newIdx]] = [fields[newIdx], fields[fieldIdx]];
    setTemplate(t => ({ ...t, fields }));
    try {
      await formsAPI.reorderFields(id, fields.map(f => f.id));
    } catch { fetchTemplate(); }
  };

  const handleUpdateMeta = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await formsAPI.updateTemplate(id, {
        name: template.name,
        description: template.description,
        category: template.category,
      });
      toast.success('Template updated');
      setShowEditMeta(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
    setSaving(false);
  };

  const handleToggleActive = async () => {
    try {
      const res = await formsAPI.updateTemplate(id, { is_active: !template.is_active });
      setTemplate(t => ({ ...t, is_active: res.data.is_active }));
      toast.success(res.data.is_active ? 'Template reactivated' : 'Template archived');
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async () => {
    try {
      const res = await formsAPI.deleteTemplate(id);
      if (res.data?.soft_deleted) {
        toast.success('Template archived (has existing submissions)');
        fetchTemplate();
      } else {
        toast.success('Template deleted');
        navigate('/forms');
      }
    } catch { toast.error('Failed to delete'); }
    setShowDeleteConfirm(false);
  };

  const openEditField = (field) => {
    const cfg = field.config || {};
    const t = field.field_type;
    const presetKey = t === 'radio'
      ? (cfg.preset || detectPresetKey(field.options || []))
      : 'pass_advisory_fail';
    const vi = cfg.visible_if || null;
    setFieldForm({
      label: field.label,
      field_type: t,
      is_required: field.is_required,
      placeholder: field.placeholder || '',
      options: (field.options || []).join('\n'),
      preset_key: presetKey,
      content_markdown: cfg.content_markdown || '',
      vis_enabled: !!vi,
      vis_field_key: vi?.field_key || '',
      vis_operator: vi?.operator || '',
      vis_value: vi?.value ?? '',
      repeatable: !!cfg.repeatable,
      repeat_label: cfg.repeat_label || '',
      max_instances: cfg.max_instances || 20,
    });
    setEditingField(field);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!template) return null;

  const fields = template.fields || [];
  // Phase 2: visibility rules can only reference fields that come BEFORE the
  // current field, so we filter the candidate list down to (a) the fields
  // before the one we're editing, or (b) all fields when adding new.
  const editingIdx = editingField ? fields.findIndex(f => f.id === editingField.id) : fields.length;
  const earlierFieldsForVisibility = fields
    .slice(0, editingIdx < 0 ? fields.length : editingIdx)
    .filter(f => VISIBILITY_ELIGIBLE_PARENT_TYPES.has(f.field_type));
  const isLayout = LAYOUT_TYPES.has(fieldForm.field_type);
  const isOption = OPTION_TYPES.has(fieldForm.field_type);
  const showCustomOptions = isOption && !(fieldForm.field_type === 'radio' && fieldForm.preset_key && fieldForm.preset_key !== 'custom');

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="form-template-editor">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/forms')} data-testid="back-to-templates"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold">{template.name}</h1>
            {!template.is_active && <Badge variant="secondary">Archived</Badge>}
            {template.category && <Badge variant="outline">{template.category}</Badge>}
          </div>
          {template.description && <p className="text-sm text-muted-foreground mt-0.5">{template.description}</p>}
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowEditMeta(true)}>Edit Details</Button>
        <Button variant="outline" size="sm" onClick={handleToggleActive}>
          {template.is_active ? <><Archive className="w-3.5 h-3.5 mr-1" /> Archive</> : <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reactivate</>}
        </Button>
        <Button variant="destructive" size="sm" onClick={() => setShowDeleteConfirm(true)}>Delete</Button>
      </div>

      {/* Fields List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Fields ({fields.length})</CardTitle>
          <Button size="sm" onClick={() => { resetFieldForm(); setShowAddField(true); }} data-testid="add-field-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Field
          </Button>
        </CardHeader>
        <CardContent>
          {fields.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No fields yet. Add your first field to build this form.</p>
          ) : (
            <div className="space-y-2">
              {fields.map((f, idx) => (
                <div key={f.id} className="flex items-center gap-2 p-3 rounded-lg border bg-background hover:bg-muted/40 transition-colors" data-testid={`field-row-${f.id}`}>
                  <div className="flex flex-col gap-0.5">
                    <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => handleReorder(idx, -1)} disabled={idx === 0}><ChevronUp className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => handleReorder(idx, 1)} disabled={idx === fields.length - 1}><ChevronDown className="w-3 h-3" /></Button>
                  </div>
                  <FieldTypeIcon type={f.field_type} />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium">{f.label}</span>
                    {f.is_required && <span className="text-destructive ml-1">*</span>}
                    {OPTION_TYPES.has(f.field_type) && f.options && f.options.length > 0 && (
                      <span className="text-xs text-muted-foreground ml-2">[{f.options.join(', ')}]</span>
                    )}
                    {f.placeholder && <span className="text-xs text-muted-foreground ml-2 italic">"{f.placeholder}"</span>}
                    {f.field_type === 'static' && f?.config?.content_markdown && (
                      <span className="text-xs text-muted-foreground ml-2 italic">{(f.config.content_markdown || '').slice(0, 60)}{(f.config.content_markdown || '').length > 60 ? '…' : ''}</span>
                    )}
                    {f.field_type === 'section_header' && f?.config?.repeatable && (
                      <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-violet-50 text-violet-700 border border-violet-200" data-testid={`repeat-badge-${f.field_key}`}>
                        ↻ repeats — "{f.config.repeat_label || 'Instance'}"
                      </span>
                    )}
                    <VisibilityBadge field={f} fields={fields} />
                  </div>
                  {!LAYOUT_TYPES.has(f.field_type) && (
                    <span className="text-xs text-muted-foreground font-mono">{f.field_key}</span>
                  )}
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditField(f)} data-testid={`edit-field-${f.id}`}><Pencil className="w-3 h-3" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteField(f.id)} data-testid={`delete-field-${f.id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Auto-Attach Settings */}
      <Card data-testid="checklist-behavior-card">
        <CardHeader>
          <CardTitle className="text-sm">Checklist Behavior</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Auto-attach to new jobs</p>
              <p className="text-xs text-muted-foreground">Automatically attach this checklist when a new job is created</p>
            </div>
            <Switch
              checked={template.is_default || false}
              onCheckedChange={async (v) => {
                try {
                  await formsAPI.updateTemplate(id, { is_default: v });
                  setTemplate(t => ({ ...t, is_default: v }));
                  toast.success(v ? 'Auto-attach enabled' : 'Auto-attach disabled');
                } catch { toast.error('Failed to update'); }
              }}
              data-testid="auto-attach-toggle"
            />
          </div>
          {template.is_default && (
            <div>
              <Label className="text-xs text-muted-foreground">Attach to job types (leave empty for all)</Label>
              <div className="mt-1.5 space-y-2">
                <Input
                  placeholder="Type a job type and press Enter..."
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      e.preventDefault();
                      const newTypes = [...(template.auto_attach_job_types || []), e.target.value.trim()];
                      try {
                        await formsAPI.updateTemplate(id, { auto_attach_job_types: newTypes });
                        setTemplate(t => ({ ...t, auto_attach_job_types: newTypes }));
                        e.target.value = '';
                      } catch { toast.error('Failed to update'); }
                    }
                  }}
                  data-testid="job-type-input"
                />
                {(template.auto_attach_job_types || []).length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {template.auto_attach_job_types.map((jt, i) => (
                      <Badge key={i} variant="secondary" className="gap-1 cursor-pointer" onClick={async () => {
                        const newTypes = template.auto_attach_job_types.filter((_, idx) => idx !== i);
                        try {
                          await formsAPI.updateTemplate(id, { auto_attach_job_types: newTypes });
                          setTemplate(t => ({ ...t, auto_attach_job_types: newTypes }));
                        } catch { toast.error('Failed'); }
                      }}>
                        {jt} <span className="text-muted-foreground">&times;</span>
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">All job types (no filter applied)</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit Field Sheet */}
      <Sheet open={showAddField || !!editingField} onOpenChange={(open) => { if (!open) { setShowAddField(false); setEditingField(null); resetFieldForm(); } }}>
        <SheetContent className="overflow-y-auto">
          <SheetHeader><SheetTitle>{editingField ? 'Edit Field' : 'Add Field'}</SheetTitle></SheetHeader>
          <form onSubmit={editingField ? handleUpdateField : handleAddField} className="space-y-4 mt-6">
            <div>
              <Label>{fieldForm.field_type === 'section_header' ? 'Section title *' : fieldForm.field_type === 'divider' ? 'Internal label (not displayed)' : 'Label *'}</Label>
              <Input
                value={fieldForm.label}
                onChange={e => setFieldForm(f => ({ ...f, label: e.target.value }))}
                placeholder={fieldForm.field_type === 'section_header' ? 'e.g. Site Conditions' : fieldForm.field_type === 'static' ? 'e.g. Pre-Inspection Notes' : 'e.g. Equipment Model'}
                autoFocus
                data-testid="field-label-input"
              />
            </div>

            <div>
              <Label>Type</Label>
              <Select value={fieldForm.field_type} onValueChange={v => setFieldForm(f => ({ ...f, field_type: v }))}>
                <SelectTrigger data-testid="field-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Input', 'Choice', 'Signature', 'Layout'].map(group => (
                    <React.Fragment key={group}>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{group}</div>
                      {FIELD_TYPES.filter(ft => ft.group === group).map(ft => (
                        <SelectItem key={ft.value} value={ft.value} data-testid={`field-type-option-${ft.value}`}>{ft.label}</SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {fieldForm.field_type === 'radio' && (
              <div data-testid="radio-preset-block">
                <Label>Preset</Label>
                <Select value={fieldForm.preset_key} onValueChange={v => setFieldForm(f => ({ ...f, preset_key: v }))}>
                  <SelectTrigger data-testid="radio-preset-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RADIO_PRESETS.map(p => (
                      <SelectItem key={p.key} value={p.key} data-testid={`radio-preset-${p.key}`}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldForm.preset_key === 'pass_advisory_fail' && (
                  <p className="text-[11px] text-muted-foreground mt-1">Pass / Advisory / Fail will display as <span className="text-emerald-600 font-medium">Pass</span> / <span className="text-amber-600 font-medium">Advisory</span> / <span className="text-rose-600 font-medium">Fail</span> color-coded pills.</p>
                )}
              </div>
            )}

            {showCustomOptions && (
              <div>
                <Label>Options (one per line) *</Label>
                <Textarea
                  value={fieldForm.options}
                  onChange={e => setFieldForm(f => ({ ...f, options: e.target.value }))}
                  placeholder={"Option A\nOption B\nOption C"}
                  rows={4}
                  data-testid="field-options-input"
                />
              </div>
            )}

            {fieldForm.field_type === 'static' && (
              <div>
                <Label>Static content (Markdown supported)</Label>
                <Textarea
                  value={fieldForm.content_markdown}
                  onChange={e => setFieldForm(f => ({ ...f, content_markdown: e.target.value }))}
                  placeholder={"**Pre-inspection notes**\n\n- Confirm site is safe before proceeding\n- Photograph any damage"}
                  rows={6}
                  data-testid="field-static-content-input"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Markdown: <code>**bold**</code>, <code>*italic*</code>, <code>- bullets</code>, <code>[link](url)</code>.</p>
              </div>
            )}

            {fieldForm.field_type === 'section_header' && (
              <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3 space-y-2" data-testid="repeat-section">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Make this section repeatable</Label>
                  <Switch
                    checked={fieldForm.repeatable}
                    onCheckedChange={(v) => setFieldForm(f => ({
                      ...f, repeatable: v,
                      ...(v ? {} : { repeat_label: '' }),
                    }))}
                    data-testid="repeat-enabled-switch"
                  />
                </div>
                {fieldForm.repeatable && (
                  <>
                    <div>
                      <Label className="text-[11px] text-muted-foreground">Instance label *</Label>
                      <Input
                        value={fieldForm.repeat_label}
                        onChange={e => setFieldForm(f => ({ ...f, repeat_label: e.target.value }))}
                        placeholder="e.g. Sliding Gate, Swing Gate, Rolling Shutter, Sectional Door"
                        data-testid="repeat-label-input"
                      />
                      <p className="text-[11px] text-muted-foreground mt-1">User will see <em>{fieldForm.repeat_label || 'Instance'} 1</em>, <em>{fieldForm.repeat_label || 'Instance'} 2</em>, …</p>
                    </div>
                    <div>
                      <Label className="text-[11px] text-muted-foreground">Max instances</Label>
                      <Input
                        type="number"
                        min={1}
                        max={50}
                        value={fieldForm.max_instances}
                        onChange={e => setFieldForm(f => ({ ...f, max_instances: Number(e.target.value) || 20 }))}
                        data-testid="repeat-max-input"
                      />
                    </div>
                    <p className="text-[11px] text-amber-600">Fields between this section header and the next one will repeat per instance. Conditional-visibility rules inside the section may reference fields earlier in the template.</p>
                  </>
                )}
              </div>
            )}

            {!isLayout && fieldForm.field_type !== 'checkbox' && fieldForm.field_type !== 'file' && fieldForm.field_type !== 'signature' && (
              <div>
                <Label>Placeholder</Label>
                <Input
                  value={fieldForm.placeholder}
                  onChange={e => setFieldForm(f => ({ ...f, placeholder: e.target.value }))}
                  placeholder="Hint text for the field"
                />
              </div>
            )}

            {!isLayout && (
              <div className="flex items-center gap-2">
                <Switch checked={fieldForm.is_required} onCheckedChange={v => setFieldForm(f => ({ ...f, is_required: v }))} data-testid="field-required-switch" />
                <Label>Required</Label>
              </div>
            )}

            {fieldForm.field_type === 'divider' && (
              <p className="text-xs text-muted-foreground bg-muted/40 rounded-md p-3">A horizontal divider line — no value collected. The label is just an internal note.</p>
            )}

            {fieldForm.field_type === 'file' && (
              <p className="text-xs text-muted-foreground bg-muted/40 rounded-md p-3" data-testid="file-field-hint">
                Photo / file upload field. Allowed types: <strong>JPG, PNG, WEBP, HEIC, GIF, PDF</strong>. Up to <strong>10 MB</strong> per file, <strong>12 files</strong> per field. Files are stored in tenant-scoped object storage and locked once the form is submitted/signed.
              </p>
            )}

            {fieldForm.field_type === 'signature' && (
              <p className="text-xs text-muted-foreground bg-muted/40 rounded-md p-3" data-testid="signature-field-hint">
                In-form signature field — captures a signer name and a drawn signature with timestamp. Add as many as you need (e.g. <em>Assessor Signature</em>, <em>Client Representative Signature</em>). This is independent of the customer-signature-link sent via email.
              </p>
            )}

            <VisibilitySection
              fieldForm={fieldForm}
              setFieldForm={setFieldForm}
              earlierFields={earlierFieldsForVisibility}
            />

            <Button type="submit" disabled={saving || !fieldForm.label.trim()} className="w-full" data-testid="save-field-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} {editingField ? 'Update Field' : 'Add Field'}
            </Button>
          </form>
        </SheetContent>
      </Sheet>

      {/* Edit Metadata Dialog */}
      <Dialog open={showEditMeta} onOpenChange={setShowEditMeta}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Template Details</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdateMeta} className="space-y-4">
            <div><Label>Name</Label><Input value={template.name} onChange={e => setTemplate(t => ({ ...t, name: e.target.value }))} /></div>
            <div><Label>Description</Label><Textarea value={template.description || ''} onChange={e => setTemplate(t => ({ ...t, description: e.target.value }))} rows={2} /></div>
            <div><Label>Category</Label><Input value={template.category || ''} onChange={e => setTemplate(t => ({ ...t, category: e.target.value }))} /></div>
            <DialogFooter>
              <Button type="submit" disabled={saving}>{saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Template?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {(template.submission_count || 0) > 0
              ? `This template has ${template.submission_count} submission(s) and will be archived instead of permanently deleted.`
              : 'This template has no submissions and will be permanently deleted.'}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}

export default FormTemplateEditor;

// ─── Phase 2 — Visibility editor section ─────────────────────────────────
function VisibilitySection({ fieldForm, setFieldForm, earlierFields }) {
  const enabled = fieldForm.vis_enabled;
  const parent = earlierFields.find(f => f.field_key === fieldForm.vis_field_key);
  const allowedOps = parent ? (VISIBILITY_OP_BY_PARENT_TYPE[parent.field_type] || []) : [];
  const op = fieldForm.vis_operator;
  const valueRequired = ['equals', 'not_equals', 'contains'].includes(op);

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3 space-y-2" data-testid="visibility-section">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {enabled
            ? <Eye className="w-3.5 h-3.5 text-emerald-600" />
            : <EyeOff className="w-3.5 h-3.5 text-slate-400" />}
          <Label className="text-xs">Conditional visibility</Label>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(v) => setFieldForm(f => ({
            ...f, vis_enabled: v,
            // Reset rule when toggling off so we don't persist stale partial state.
            ...(v ? {} : { vis_field_key: '', vis_operator: '', vis_value: '' }),
          }))}
          data-testid="visibility-enabled-switch"
        />
      </div>

      {!enabled && (
        <p className="text-[11px] text-muted-foreground">Always visible. Toggle on to show this field only when an earlier field's answer matches a condition.</p>
      )}

      {enabled && earlierFields.length === 0 && (
        <p className="text-[11px] text-amber-600">Add a data-entry field BEFORE this one to use as a controlling answer.</p>
      )}

      {enabled && earlierFields.length > 0 && (
        <div className="space-y-2">
          <div>
            <Label className="text-[11px] text-muted-foreground">Show only when…</Label>
            <Select
              value={fieldForm.vis_field_key || ''}
              onValueChange={(v) => setFieldForm(f => ({
                ...f, vis_field_key: v, vis_operator: '', vis_value: '',
              }))}
            >
              <SelectTrigger data-testid="visibility-parent-select"><SelectValue placeholder="Pick controlling field..." /></SelectTrigger>
              <SelectContent>
                {earlierFields.map(f => (
                  <SelectItem key={f.field_key} value={f.field_key} data-testid={`visibility-parent-${f.field_key}`}>
                    {f.label} <span className="text-muted-foreground text-[10px]">({f.field_type})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {parent && (
            <div>
              <Label className="text-[11px] text-muted-foreground">Operator</Label>
              <Select
                value={op || ''}
                onValueChange={(v) => setFieldForm(f => ({ ...f, vis_operator: v, vis_value: '' }))}
              >
                <SelectTrigger data-testid="visibility-operator-select"><SelectValue placeholder="Pick operator..." /></SelectTrigger>
                <SelectContent>
                  {allowedOps.map(o => (
                    <SelectItem key={o} value={o} data-testid={`visibility-operator-${o}`}>
                      {OPERATOR_LABELS[o] || o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {parent && valueRequired && (
            <VisibilityValueInput
              parent={parent}
              op={op}
              value={fieldForm.vis_value}
              onChange={(v) => setFieldForm(f => ({ ...f, vis_value: v }))}
            />
          )}

          {parent && op && (
            <p className="text-[11px] text-muted-foreground italic">
              Will show when <strong>{parent.label}</strong> {OPERATOR_LABELS[op] || op}
              {valueRequired ? <> <strong>{String(fieldForm.vis_value || '…')}</strong></> : ''}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function VisibilityValueInput({ parent, op, value, onChange }) {
  // For select/radio/checkbox_group — present a dropdown of the parent's options.
  if (['select', 'radio', 'checkbox_group'].includes(parent.field_type)) {
    return (
      <div>
        <Label className="text-[11px] text-muted-foreground">Value</Label>
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger data-testid="visibility-value-select"><SelectValue placeholder="Pick value..." /></SelectTrigger>
          <SelectContent>
            {(parent.options || []).map(o => (
              <SelectItem key={o} value={o} data-testid={`visibility-value-${o}`}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (parent.field_type === 'number') {
    return (
      <div>
        <Label className="text-[11px] text-muted-foreground">Value</Label>
        <Input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          data-testid="visibility-value-input"
        />
      </div>
    );
  }
  if (parent.field_type === 'date') {
    return (
      <div>
        <Label className="text-[11px] text-muted-foreground">Date</Label>
        <Input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid="visibility-value-input" />
      </div>
    );
  }
  // text / textarea
  return (
    <div>
      <Label className="text-[11px] text-muted-foreground">Value</Label>
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={op === 'contains' ? 'Substring to match' : 'Exact value'}
        data-testid="visibility-value-input"
      />
    </div>
  );
}

function VisibilityBadge({ field, fields }) {
  const rule = field?.config?.visible_if;
  if (!rule) return null;
  const parent = (fields || []).find(f => f.field_key === rule.field_key);
  if (!parent) return null;
  const opLabel = OPERATOR_LABELS[rule.operator] || rule.operator;
  const valueText = ['equals', 'not_equals', 'contains'].includes(rule.operator)
    ? ` "${rule.value}"` : '';
  return (
    <span
      className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-amber-50 text-amber-800 border border-amber-200"
      title={`Visible when ${parent.label} ${opLabel}${valueText}`}
      data-testid={`visibility-badge-${field.field_key}`}
    >
      <Eye className="w-2.5 h-2.5" /> if {parent.label} {opLabel}{valueText}
    </span>
  );
}
